'use client'

import { SelectOption } from '../../../../types'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
    findNextEnabledOptionIndex,
    findOptionIndexByLabel,
    findPreviousEnabledOptionIndex,
    TYPE_AHEAD_RESET_MS,
} from '../_helpers/selectHelpers'

export interface ComboboxListboxContext {
    closeListbox: () => void
}

export interface UseComboboxListboxParams {
    disabled: boolean
    className?: string
    classes?: string
    filteredOptions: SelectOption[]
    onActivateOption: (value: string | number, context: ComboboxListboxContext) => void
}

/**
 * Shared hook for single and multi-select combobox listbox behaviour.
 */
export function useComboboxListbox({
    disabled,
    className = '',
    classes = '',
    filteredOptions,
    onActivateOption,
}: UseComboboxListboxParams) {
    const [isOpen, setIsOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(0)
    const searchTypeAheadRef = useRef({ buffer: '', lastKeyTime: 0 })
    const selectContainerRef = useRef<HTMLDivElement>(null)
    const selectInputRef = useRef<HTMLSelectElement>(null)
    const listBoxTriggerRef = useRef<HTMLButtonElement>(null)
    const listboxId = useId()

    useEffect(() => {
        if (!disabled) return
        setIsOpen(false)
        if (listBoxTriggerRef.current && document.activeElement === listBoxTriggerRef.current) {
            ;(document.activeElement as HTMLElement)?.blur()
        }
    }, [disabled])

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (mouseEvent: MouseEvent) => {
            if (selectContainerRef.current && !selectContainerRef.current.contains(mouseEvent.target as Node)) {
                setIsOpen(false)
                if (!disabled && listBoxTriggerRef.current) listBoxTriggerRef.current.focus()
            }
        }
        const documentOutsideClickAbortController = new AbortController()
        document.addEventListener('mousedown', handleClickOutside, {
            signal: documentOutsideClickAbortController.signal,
        })
        return () => documentOutsideClickAbortController.abort()
    }, [isOpen, disabled])

    useEffect(() => {
        if (filteredOptions.length > 0 && focusedIndex >= filteredOptions.length) {
            setFocusedIndex(filteredOptions.length - 1)
        }
    }, [filteredOptions.length, focusedIndex])

    useEffect(() => {
        if (isOpen && filteredOptions.length > 0) {
            const optionId = `${listboxId}-option-${focusedIndex}`
            document.getElementById(optionId)?.scrollIntoView({ block: 'nearest' })
        }
    }, [isOpen, focusedIndex, filteredOptions.length, listboxId])

    const openListbox = useCallback(
        (getInitialFocusedIndex: () => number) => {
            if (filteredOptions.length === 0) return
            setIsOpen(true)
            setFocusedIndex(findNextEnabledOptionIndex(filteredOptions, getInitialFocusedIndex()))
        },
        [filteredOptions],
    )

    const closeListbox = useCallback(() => {
        setIsOpen(false)
        if (!disabled && listBoxTriggerRef.current) listBoxTriggerRef.current.focus()
    }, [disabled])

    const handleComboboxKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (disabled) return
            const pressedKey = event.key
            const now = Date.now()
            const isPrintableChar = pressedKey.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey

            if (isOpen) {
                switch (pressedKey) {
                    case 'Escape':
                        event.preventDefault()
                        closeListbox()
                        break
                    case 'Tab':
                        closeListbox()
                        break
                    case 'Enter':
                    case ' ': {
                        event.preventDefault()
                        const focusedOption = filteredOptions[focusedIndex]
                        if (focusedOption) onActivateOption(focusedOption.value!, { closeListbox })
                        break
                    }
                    case 'ArrowDown':
                        event.preventDefault()
                        setFocusedIndex((index) => findNextEnabledOptionIndex(filteredOptions, index + 1))
                        break
                    case 'ArrowUp':
                        event.preventDefault()
                        setFocusedIndex((index) => findPreviousEnabledOptionIndex(filteredOptions, index - 1))
                        break
                    case 'Home':
                        event.preventDefault()
                        setFocusedIndex(findNextEnabledOptionIndex(filteredOptions, 0))
                        break
                    case 'End':
                        event.preventDefault()
                        setFocusedIndex(findPreviousEnabledOptionIndex(filteredOptions, filteredOptions.length - 1))
                        break
                    default:
                        if (isPrintableChar) {
                            event.preventDefault()
                            const { buffer, lastKeyTime } = searchTypeAheadRef.current
                            const newBuffer =
                                now - lastKeyTime > TYPE_AHEAD_RESET_MS ? pressedKey : buffer + pressedKey.toLowerCase()
                            searchTypeAheadRef.current = { buffer: newBuffer, lastKeyTime: now }
                            const nextIndex = findOptionIndexByLabel(filteredOptions, newBuffer, focusedIndex + 1)
                            if (nextIndex !== focusedIndex) setFocusedIndex(nextIndex)
                        }
                }
            } else {
                switch (pressedKey) {
                    case 'Enter':
                    case ' ':
                    case 'ArrowDown':
                        event.preventDefault()
                        openListbox(() => 0)
                        break
                    case 'End':
                    case 'ArrowUp':
                        event.preventDefault()
                        openListbox(() => filteredOptions.length - 1)
                        break
                    case 'Home':
                        event.preventDefault()
                        openListbox(() => 0)
                        break
                    default:
                        if (isPrintableChar) {
                            event.preventDefault()
                            searchTypeAheadRef.current = { buffer: pressedKey.toLowerCase(), lastKeyTime: now }
                            openListbox(() => findOptionIndexByLabel(filteredOptions, pressedKey, 0))
                        }
                }
            }
        },
        [disabled, isOpen, focusedIndex, filteredOptions, onActivateOption, openListbox, closeListbox],
    )

    const baseClasses =
        `w-full bg-white border border-shade-200 p-3 disabled:bg-disabled disabled:text-text-disabled ${className} ${classes}`.trim()
    const focusedOptionId = isOpen && filteredOptions[focusedIndex] ? `${listboxId}-option-${focusedIndex}` : undefined

    return {
        selectContainerRef,
        selectInputRef,
        listBoxTriggerRef,
        listboxId,
        isOpen,
        focusedIndex,
        openListbox,
        closeListbox,
        handleComboboxKeyDown,
        baseClasses,
        focusedOptionId,
    }
}
